import { Component, inject, Inject, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, Observable, of, Subscription, withLatestFrom } from 'rxjs';
import {
  Context,
  Privacy,
  PrivacyLevel,
  SalesCoachConfig,
  SalesCoachUserConfig,
} from '@vendasta/protos/meeting_analysis/v1';
import * as SalesCoachConfigService from '@vendasta/protos/meeting_analysis/v1/sales-coach-config-service';
import { APPLICATION_CONTEXT_TOKEN, CONTEXT_DATA_TOKEN, ContextData } from '../../data-providers/providers';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { catchError, filter, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { MatDialog } from '@angular/material/dialog';
import { PrivacyExplanationDialogComponent } from './privacy-explanation-dialog/privacy-explanation-dialog.component';
import { MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$ } from '@galaxy/meeting-analysis/static';
import { ApplicationContextPropertiesSet } from '@vendasta/meetings';
import { UnsavedChangesGuard } from '../../shared/unsaved-changes-guard.service';

export interface UserConfigSettings {
  joinMeetings: boolean;
  internalMeetingVisibility: PrivacyLevel;
  externalMeetingVisibility: PrivacyLevel;
}

export interface NamespaceConfigSettings {
  joinMeetings: boolean;
  notetakerName: string;
  showRecordingConsentMessages: boolean;
}

@Component({
  selector: 'meeting-scheduler-sales-coach-user-config-settings',
  templateUrl: './sales-coach-user-settings.component.html',
  styleUrls: ['./sales-coach-user-settings.component.scss'],
  standalone: false,
})
export class SalesCoachUserSettingsComponent implements OnDestroy {
  @Input() showNamespaceSettings = true;
  @Input() showUserConfigSaveButton = true;

  userConfigForm: FormGroup<{
    joinMeetings: FormControl<boolean>;
    internalMeetingVisibility: FormControl<PrivacyLevel>;
    externalMeetingVisibility: FormControl<PrivacyLevel>;
  }>;
  namespaceConfigForm: FormGroup<{
    joinMeetings: FormControl<boolean>;
    notetakerName: FormControl<string>;
    showRecordingConsentMessages: FormControl<boolean>;
  }> = new FormGroup({
    joinMeetings: new FormControl(false),
    notetakerName: new FormControl(''),
    showRecordingConsentMessages: new FormControl(false),
  });
  isSavingUserConfigForm = false;
  isSavingNamespaceConfigForm = false;
  PrivacyLevel = PrivacyLevel;

  salesCoachConfig$: Observable<SalesCoachConfig>;
  partnerName$ = inject(MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$);
  namespace$: Observable<string>;
  userId$: Observable<string>;
  applicationContext$: Observable<ApplicationContextPropertiesSet>;

  joinMeetingsSaveSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private snackbarService: SnackbarService,
    private unsavedChangesGuard: UnsavedChangesGuard,
    private meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private translateService: TranslateService,
    private confirmationModal: OpenConfirmationModalService,
    private dialog: MatDialog,
    @Inject(CONTEXT_DATA_TOKEN)
    private readonly contextData$: Observable<ContextData>,
    @Inject(APPLICATION_CONTEXT_TOKEN)
    applicationContext$: Observable<ApplicationContextPropertiesSet>,
  ) {
    this.applicationContext$ = applicationContext$;
    this.userConfigForm = this.formBuilder.group({
      joinMeetings: new FormControl<boolean>(false),
      internalMeetingVisibility: new FormControl<PrivacyLevel>(PrivacyLevel.PRIVACY_LEVEL_PRIVATE),
      externalMeetingVisibility: new FormControl<PrivacyLevel>(PrivacyLevel.PRIVACY_LEVEL_OPEN),
    });
    this.initializeObservables();
    this.loadExistingSettings();
    // Register forms to create a warning when leaving the page with changes in these forms
    this.unsavedChangesGuard.registerForms([
      this.userConfigForm.controls.internalMeetingVisibility,
      this.userConfigForm.controls.externalMeetingVisibility,
      this.namespaceConfigForm,
    ]);
  }

  userJoinMeetingsToggleChanged(): void {
    // Mark the form as pristine so we don't get unnecessary unsaved changes warnings when navigating away
    this.userConfigForm.markAsPristine();
  }

  private initializeObservables(): void {
    this.namespace$ = this.contextData$.pipe(map((data) => data.namespace));

    this.userId$ = of(this.meetingSchedulerStoreService.getUserId()).pipe(
      filter(Boolean),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.salesCoachConfig$ = this.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return SalesCoachConfigService.get$({ namespace: namespace });
      }),
      map((resp) => {
        return resp?.salesCoachConfig;
      }),
      catchError((err) => {
        if (err.status !== 404) {
          this.snackbarService.openErrorSnack(
            this.translateService.instant('MEETING_SCHEDULER.SALES_COACH_CONFIG.LOAD_ERROR'),
          );
        }
        return of({ joinMeetings: false } as SalesCoachConfig);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private loadExistingSettings(): void {
    const salesCoachUserConfig$: Observable<SalesCoachUserConfig> = combineLatest([this.namespace$, this.userId$]).pipe(
      take(1),
      switchMap(([namespace, userId]) =>
        SalesCoachConfigService.getUserConfig$({
          namespace: namespace,
          userId: userId,
        }),
      ),
      map((resp) => {
        return resp?.salesCoachUserConfig;
      }),
      catchError((err) => {
        if (err.status !== 404) {
          this.snackbarService.openErrorSnack(
            this.translateService.instant('MEETING_SCHEDULER.SALES_COACH_CONFIG.LOAD_ERROR'),
          );
        }
        return of({ joinMeetings: false } as SalesCoachUserConfig);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    combineLatest([this.salesCoachConfig$, salesCoachUserConfig$]).subscribe({
      next: ([salesCoachConfig, salesCoachUserConfig]) => {
        if (!salesCoachConfig || !salesCoachUserConfig) {
          return;
        }
        const privacy = salesCoachUserConfig.privacy;
        this.userConfigForm.patchValue({
          joinMeetings: salesCoachUserConfig.joinMeetings,
          internalMeetingVisibility: privacy?.internalMeetings || PrivacyLevel.PRIVACY_LEVEL_PRIVATE,
          externalMeetingVisibility: privacy?.externalMeetings || PrivacyLevel.PRIVACY_LEVEL_OPEN,
        });
        if (!salesCoachUserConfig.joinMeetings) {
          this.userConfigForm.controls.internalMeetingVisibility.disable();
          this.userConfigForm.controls.externalMeetingVisibility.disable();
        }
        this.namespaceConfigForm.patchValue({
          joinMeetings: salesCoachConfig.joinMeetings,
          notetakerName: salesCoachConfig.notetakerName,
          showRecordingConsentMessages: salesCoachConfig.showRecordingConsentMessages,
        });
        this.setupJoinMeetingsSave();
      },
    });
  }

  SaveUserConfigSettings(): void {
    if (!this.userConfigForm.valid) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('MEETING_SCHEDULER.PRIVACY_SETTINGS.FORM_INVALID'),
      );
      return;
    }

    const settings: UserConfigSettings = this.userConfigForm.getRawValue();
    this.isSavingUserConfigForm = true;

    combineLatest([this.namespace$, this.userId$, this.applicationContext$])
      .pipe(
        take(1),
        switchMap(([namespace, userId, applicationContext]) => {
          const privacy: Privacy = {
            internalMeetings: settings.internalMeetingVisibility,
            externalMeetings: settings.externalMeetingVisibility,
          };

          const contextObj = applicationContext as any;
          const userContext = contextObj.user_context;
          const context =
            userContext === 'SMB' ? Context.SALES_COACH_CONFIG_CONTEXT_SMB : Context.SALES_COACH_CONFIG_CONTEXT_PARTNER;

          return SalesCoachConfigService.upsertUserConfig$({
            namespace,
            userId,
            privacy,
            joinMeetings: settings.joinMeetings,
            context,
          });
        }),
        finalize(() => {
          this.isSavingUserConfigForm = false;
        }),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack(
            this.translateService.instant('MEETING_SCHEDULER.USER_CONFIG_SETTINGS.SAVE_SUCCESS'),
          );
          this.userConfigForm.markAsPristine(); // Mark the form as pristine so we don't get unnecessary unsaved changes warnings when navigating away
        },
        error: () => {
          this.snackbarService.openErrorSnack(
            this.translateService.instant('MEETING_SCHEDULER.USER_CONFIG_SETTINGS.SAVE_ERROR'),
          );
        },
      });
  }

  SaveNamespaceConfigSettings(): void {
    if (!this.namespaceConfigForm.valid) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('MEETING_SCHEDULER.PRIVACY_SETTINGS.FORM_INVALID'),
      );
      return;
    }

    const settings: NamespaceConfigSettings = this.namespaceConfigForm.getRawValue();
    this.isSavingNamespaceConfigForm = true;

    this.partnerName$
      .pipe(
        take(1),
        switchMap((partnerName) => {
          return this.confirmationModal.openModal({
            type: 'warn',
            title: this.translateService.instant('MEETING_SCHEDULER.NAMESPACE_SETTINGS.MODAL_TITLE'),
            message: this.translateService.instant('MEETING_SCHEDULER.NAMESPACE_SETTINGS.MODAL_DESCRIPTION', {
              partnerName: partnerName,
            }),
            confirmButtonText: 'COMMON.SAVE',
          });
        }),
        withLatestFrom(this.namespace$),
        switchMap(([confirmed, namespace]) => {
          if (confirmed) {
            return SalesCoachConfigService.update$({
              namespace: namespace,
              notetakerName: settings.notetakerName,
              showRecordingConsentMessages: settings.showRecordingConsentMessages,
              fieldMask: { paths: ['NotetakerName', 'ShowRecordingConsentMessages'] },
            });
          }
          return of(false);
        }),
      )
      .subscribe({
        next: (resp) => {
          this.isSavingNamespaceConfigForm = false;
          if (resp) {
            this.snackbarService.openSuccessSnack(
              this.translateService.instant('MEETING_SCHEDULER.USER_CONFIG_SETTINGS.SAVE_SUCCESS'),
            );
            this.namespaceConfigForm.markAsPristine(); // Mark the form as pristine so we don't get unnecessary unsaved changes warnings when navigating away
          }
        },
        error: (err) => {
          this.isSavingNamespaceConfigForm = false;
          const isRateLimitError = err?.status === 429;
          const errorKey = isRateLimitError
            ? 'MEETING_SCHEDULER.USER_CONFIG_SETTINGS.SAVE_ERROR_RATE_LIMIT'
            : 'MEETING_SCHEDULER.USER_CONFIG_SETTINGS.SAVE_ERROR';
          this.snackbarService.openErrorSnack(this.translateService.instant(errorKey));
        },
      });
    return;
  }

  private setupJoinMeetingsSave(): void {
    this.joinMeetingsSaveSubscription = this.userConfigForm.controls.joinMeetings.valueChanges.subscribe({
      next: () => {
        this.SaveUserConfigSettings();
      },
    });
  }

  learnMoreClicked(): void {
    this.dialog.open(PrivacyExplanationDialogComponent, { width: '500px' });
  }

  ngOnDestroy(): void {
    if (this.joinMeetingsSaveSubscription) {
      this.joinMeetingsSaveSubscription.unsubscribe();
    }
  }
}
